import React, { ReactNode, useEffect } from 'react';
import { useUser } from '@clerk/clerk-react';
import { useSiteRegion } from 'utils/hooks/useSiteSection';

type AnalyticsProviderProps = {
  children: ReactNode;
};

declare global {
  interface Window {
    clarity?: (event: string, payload: any) => void;
    dataLayer?: Record<string, any>[];
    fcWidget?: any;
  }
}

export const AnalyticsProvider: React.FC<AnalyticsProviderProps> = ({ children }) => {
  const { user } = useUser();
   const region = useSiteRegion();

  useEffect(() => {
    if (!user?.id || !user?.emailAddresses || user.emailAddresses.length === 0) return;

    const userId = user.id;
    const email = user.emailAddresses[0].emailAddress;

    // Microsoft Clarity
    if (typeof window !== 'undefined' && typeof window.clarity === 'function') {
      window.clarity('identify', email);
    }

    if (typeof window !== 'undefined') {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        userId: userId,
      });
    }

    // Cleanup function to prevent dataLayer from growing indefinitely
    return () => {
      if (typeof window !== 'undefined' && window.dataLayer) {
        // Keep only the last 100 entries to prevent memory leaks
        if (window.dataLayer.length > 100) {
          window.dataLayer = window.dataLayer.slice(-100);
        }
      }
    };
  }, [user]);

  useEffect(() => {
    if( region !== 'au' ) return;
    initFreshchat(user);

    // Cleanup function for Freshchat
    return () => {
      if (typeof window !== 'undefined' && window.fcWidget) {
        try {
          window.fcWidget.destroy();
          // Clear the widget reference
          delete window.fcWidget;
        } catch (e) {
          // Ignore errors if widget is already destroyed
        }
      }
    };
  }, [user, region]);

  return <>{children}</>;
};

function initFreshchat(user?: ReturnType<typeof useUser>['user']) {
  const userId = user?.id;
  const email = user?.emailAddresses?.[0]?.emailAddress || '';
  const firstName = user?.firstName || '';

  const initializeFreshchat = () => {
    if (!window.fcWidget) return;

    window.fcWidget.init({
      token: 'c46b4947-1b87-48be-9e25-9ba9c505d35d',
      host: 'https://abcopro.freshchat.com',
    });

    if (userId && email) {
      window.fcWidget.setExternalId(userId);
      window.fcWidget.user.setFirstName(firstName);
      window.fcWidget.user.setEmail(email);
      window.fcWidget.user.setProperties({
        plan: 'Abconet',
        status: 'Active',
      });
    }
  };

  if (typeof window === 'undefined') return;

  if (window.fcWidget) {
    initializeFreshchat();
  } else if (!document.getElementById('Freshchat-js-sdk')) {
    const script = document.createElement('script');
    script.id = 'Freshchat-js-sdk';
    script.async = true;
    script.src = 'https://abcopro.freshchat.com/js/widget.js';
    script.onload = initializeFreshchat;
    document.head.appendChild(script);
  }
}
